import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBookOpen, 
  faRunning, 
  faChartLine, 
  faUser, 
  faSignOutAlt,
  faSun,
  faMoon,
  faCog,
  faCreditCard,
  faTimes,
  faBars,
  faTachometerAlt,
  faChevronDown,
  faAdjust,
  faLightbulb,
  faRobot,
  faChartBar,
  faClone
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import './Navigation.css';

const Navigation = () => {
  const [isNavVisible, setIsNavVisible] = useState(window.innerWidth > 768);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  
  // Set initial navigation state based on screen size
  useEffect(() => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    setIsNavVisible(!mobile); // Show nav on desktop, hide on mobile by default
  }, []);

  // Close menu when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setIsNavVisible(false);
      document.body.classList.remove('nav-open');
    }
  }, [location, isMobile]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsNavVisible(!mobile); // Show nav on desktop, hide on mobile
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleNav = () => {
    setIsNavVisible(!isNavVisible);
    // Prevent body scroll on mobile when nav is open
    if (isMobile) {
      if (!isNavVisible) {
        document.body.classList.add('nav-open');
      } else {
        document.body.classList.remove('nav-open');
      }
    }
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  // Extract first letter of user's name for avatar - prefer firstName over email
  const userInitial = user?.firstName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U';
  // Use firstName if available, otherwise fall back to email prefix
  const userName = user?.firstName || user?.email?.split('@')[0] || 'User';

  return (
    <div className="navigation-container">
      {/* Mobile nav toggle button */}
      <button 
        className="dashboard-toggle-button" 
        onClick={toggleNav}
        aria-label="Toggle navigation"
      >
        <FontAwesomeIcon icon={faBars} className="nav-icon" size="sm" />
      </button>

      {/* Nav overlay for mobile */}
      <div 
        className={`nav-overlay ${isMobile && isNavVisible ? 'visible' : ''}`} 
        onClick={() => {
          setIsNavVisible(false);
          if (isMobile) {
            document.body.classList.remove('nav-open');
          }
        }}
      />

      {/* Navigation sidebar */}
      <nav className={`app-nav ${isNavVisible ? 'visible' : ''}`}>
        {/* Logo - Different version based on nav state and theme */}
        <div className="nav-logo-container">
          <img 
            src="/examfliplogo.png" 
            alt="ExamFlip Logo" 
            className="nav-logo-collapsed" 
          />
          <img 
            src={isDarkMode ? "/dark_green.png" : "/dark_green.png"} 
            alt="ExamFlip Logo" 
            className="nav-logo-expanded" 
          />
        </div>        {/* User profile */}
        <div className="nav-user" onClick={toggleUserMenu}>
          <div className="nav-user-avatar">{userInitial}</div>
          <span className="nav-user-name">{userName}</span>
          <FontAwesomeIcon icon={faChevronDown} className="nav-user-icon" size="sm" />
        </div>

        {/* User menu */}
        {isUserMenuOpen && (
          <ul className="nav-user-menu">
            <li>
              <NavLink to="/billing">
                <FontAwesomeIcon icon={faCreditCard} className="nav-icon" size="sm" />
                <span className="nav-label">Billing</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/usage">
                <FontAwesomeIcon icon={faChartBar} className="nav-icon" size="sm" />
                <span className="nav-label">Usage</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/settings">
                <FontAwesomeIcon icon={faCog} className="nav-icon" size="sm" />
                <span className="nav-label">Settings</span>
              </NavLink>
            </li>            <li>
              <button 
                onClick={handleLogout}
                className="nav-logout-button"
              >
                <FontAwesomeIcon icon={faSignOutAlt} className="nav-icon" size="sm" />
                <span className="nav-label">Sign Out</span>
              </button>
            </li>
          </ul>
        )}

        {/* Main menu */}
        <ul className="nav-main-menu">
          <li>
            <NavLink 
              to="/dashboard"
              className={({ isActive }) => isActive ? "active" : ""}
            >
              <FontAwesomeIcon icon={faTachometerAlt} className="nav-icon" size="sm" />
              <span className="nav-label">Dashboard</span>
            </NavLink>
          </li>
          <li>
            <NavLink 
              to="/practice-test"
              className={({ isActive }) => isActive ? "active" : ""}
            >
              <FontAwesomeIcon icon={faBookOpen} className="nav-icon" size="sm" />
              <span className="nav-label">Practice Test</span>
            </NavLink>
          </li>
          <li>
            <NavLink 
              to="/simulate-exam"
              className={({ isActive }) => isActive ? "active" : ""}
            >
              <FontAwesomeIcon icon={faRunning} className="nav-icon" size="sm" />
              <span className="nav-label">Simulate Exam</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/performance-history"
              className={({ isActive }) => isActive ? "active" : ""}
            >
              <FontAwesomeIcon icon={faChartLine} className="nav-icon" size="sm" />
              <span className="nav-label">Performance</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/flashcards"
              className={({ isActive }) => isActive ? "active" : ""}
            >
              <FontAwesomeIcon icon={faClone} className="nav-icon" size="sm" />
              <span className="nav-label">Flashcards</span>
            </NavLink>
          </li>
          {/* AI Chat option removed */}
          <li>
            <button 
              onClick={toggleTheme} 
              className="theme-toggle-nav"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              <FontAwesomeIcon 
                icon={isDarkMode ? faSun : faMoon} 
                className="nav-icon" 
                size="sm"
              />
              <span className="nav-label">
                {isDarkMode ? "Light Mode" : "Dark Mode"}
              </span>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Navigation;